import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Building2,
  Plus,
  Trash2,
  Edit2,
  X,
  Check,
  ExternalLink,
  Info,
} from "lucide-react";
import toast from "react-hot-toast";
import { apiService, extractApiError } from "../services/api";
import { CRAEconomicOperator, CRAEconomicOperatorRole } from "../types";

interface EconomicOperatorsCardProps {
  productId: string;
  /**
   * Compact mode renders a tighter layout (no large header card) suitable for
   * embedding inside another panel (e.g. Q1 "Add legal entity and its role").
   */
  compact?: boolean;
  /** When true, hides add/edit/delete controls (view-only). */
  readOnly?: boolean;
  /** Optional callback fired whenever the list changes. */
  onChange?: (ops: CRAEconomicOperator[]) => void;
}

const ROLE_OPTIONS: { value: CRAEconomicOperatorRole; label: string }[] = [
  { value: "manufacturer", label: "Manufacturer" },
  { value: "importer", label: "Importer" },
  { value: "distributor", label: "Distributor" },
];

interface DraftRow {
  legalEntityName: string;
  corporateId: string;
  role: CRAEconomicOperatorRole;
  mainEstablishment: string;
}

const emptyDraft: DraftRow = {
  legalEntityName: "",
  corporateId: "",
  role: "manufacturer",
  mainEstablishment: "",
};

export default function EconomicOperatorsCard({
  productId,
  compact = false,
  readOnly = false,
  onChange,
}: EconomicOperatorsCardProps) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftRow>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftRow>(emptyDraft);

  const queryKey = ["economic-operators", productId];

  const { data, isLoading } = useQuery(
    queryKey,
    () => apiService.listCRAEconomicOperators(productId),
    {
      enabled: !!productId,
      onSuccess: (res) => {
        const items = (res?.data as any)?.data?.items ?? [];
        onChange?.(items);
      },
    },
  );
  const operators: CRAEconomicOperator[] =
    (data?.data as any)?.data?.items ?? [];

  const createMutation = useMutation(
    (payload: DraftRow) =>
      apiService.createCRAEconomicOperator(productId, payload),
    {
      onSuccess: () => {
        toast.success("Economic operator added");
        setAdding(false);
        setDraft(emptyDraft);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (err) => {
        toast.error(extractApiError(err, "Failed to add economic operator"));
      },
    },
  );

  const updateMutation = useMutation(
    ({ opId, payload }: { opId: string; payload: Record<string, unknown> }) =>
      apiService.updateCRAEconomicOperator(productId, opId, payload),
    {
      onSuccess: () => {
        toast.success("Economic operator updated");
        setEditingId(null);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (err) => {
        toast.error(extractApiError(err, "Failed to update economic operator"));
      },
    },
  );

  const deleteMutation = useMutation(
    (opId: string) => apiService.deleteCRAEconomicOperator(productId, opId),
    {
      onSuccess: () => {
        toast.success("Economic operator removed");
        queryClient.invalidateQueries(queryKey);
      },
      onError: (err) => {
        toast.error(extractApiError(err, "Failed to delete economic operator"));
      },
    },
  );

  // Build a sanitised payload, only including `mainEstablishment` for manufacturer rows.
  const buildPayload = (d: DraftRow) => ({
    legalEntityName: d.legalEntityName.trim(),
    corporateId: d.corporateId.trim(),
    role: d.role,
    mainEstablishment:
      d.role === "manufacturer" && d.mainEstablishment.trim() !== ""
        ? d.mainEstablishment.trim()
        : null,
  });

  const isDraftValid = (d: DraftRow) =>
    d.legalEntityName.trim() !== "" &&
    d.corporateId.trim() !== "" &&
    (d.role !== "manufacturer" || d.mainEstablishment.trim() !== "");

  const handleStartEdit = (op: CRAEconomicOperator) => {
    setEditingId(op.id);
    setEditDraft({
      legalEntityName: op.legalEntityName,
      corporateId: op.corporateId,
      role: op.role,
      mainEstablishment: op.mainEstablishment ?? "",
    });
  };

  const wrapperClass = compact
    ? "rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50/30 dark:bg-blue-900/10 p-3 sm:p-4"
    : "rounded-lg border border-indigo-200 dark:border-indigo-800/40 bg-gradient-to-r from-indigo-50/80 to-white dark:from-indigo-900/15 dark:to-gray-900 px-3.5 py-3 sm:p-4";

  return (
    <div className={wrapperClass}>
      {!compact && (
        <div className="flex items-start gap-2.5 mb-3">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0 shadow-sm">
            <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 mb-0.5">
              CRA Role Information
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Please find legal entity details via{" "}
              <a
                href="https://sish.sandvik.com/legalentities"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 dark:text-indigo-400 font-medium underline hover:text-indigo-700 dark:hover:text-indigo-300 inline-flex items-center gap-0.5"
              >
                Sandvik Information Service Hub
                <ExternalLink className="h-3 w-3" />
              </a>
              . A product can have several economic operators added — each
              operator can have only one role.
            </p>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <>
          {operators.length === 0 && !adding && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 italic mb-3">
              <Info className="h-4 w-4" />
              No economic operators added yet.
            </div>
          )}

          {operators.length > 0 && (
            <div className="space-y-2 mb-3">
              {operators.map((op) =>
                editingId === op.id ? (
                  <OperatorEditor
                    key={op.id}
                    draft={editDraft}
                    setDraft={setEditDraft}
                    onCancel={() => setEditingId(null)}
                    onSave={() => {
                      if (!isDraftValid(editDraft)) {
                        toast.error("Please fill in all required fields");
                        return;
                      }
                      updateMutation.mutate({
                        opId: op.id,
                        payload: buildPayload(editDraft),
                      });
                    }}
                    saving={updateMutation.isLoading}
                  />
                ) : (
                  <OperatorRow
                    key={op.id}
                    op={op}
                    readOnly={readOnly}
                    onEdit={() => handleStartEdit(op)}
                    onDelete={() => {
                      if (window.confirm(`Remove ${op.legalEntityName}?`)) {
                        deleteMutation.mutate(op.id);
                      }
                    }}
                  />
                ),
              )}
            </div>
          )}

          {!readOnly &&
            (adding ? (
              <OperatorEditor
                draft={draft}
                setDraft={setDraft}
                onCancel={() => {
                  setAdding(false);
                  setDraft(emptyDraft);
                }}
                onSave={() => {
                  if (!isDraftValid(draft)) {
                    toast.error("Please fill in all required fields");
                    return;
                  }
                  createMutation.mutate(draft);
                }}
                saving={createMutation.isLoading}
              />
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                <Plus className="h-4 w-4" />
                {operators.length === 0
                  ? "Add legal entity and its role"
                  : "Add another economic operator"}
              </button>
            ))}
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────────

function OperatorRow({
  op,
  readOnly,
  onEdit,
  onDelete,
}: {
  op: CRAEconomicOperator;
  readOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {op.legalEntityName}
          </span>
          <RoleBadge role={op.role} />
        </div>
        <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
          Corporate ID: <span className="font-mono">{op.corporateId}</span>
        </div>
        {op.role === "manufacturer" && op.mainEstablishment && (
          <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
            Main establishment: {op.mainEstablishment}
          </div>
        )}
      </div>
      {!readOnly && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            title="Edit"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 dark:text-red-400"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: CRAEconomicOperatorRole }) {
  const styles: Record<CRAEconomicOperatorRole, string> = {
    manufacturer:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    importer:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    distributor: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${styles[role]}`}
    >
      {role}
    </span>
  );
}

function OperatorEditor({
  draft,
  setDraft,
  onCancel,
  onSave,
  saving,
}: {
  draft: DraftRow;
  setDraft: (d: DraftRow) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="rounded-md border border-indigo-300 dark:border-indigo-700 bg-white dark:bg-gray-900 p-3 space-y-2.5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        <Field
          label="Legal entity name"
          required
          value={draft.legalEntityName}
          onChange={(v) => setDraft({ ...draft, legalEntityName: v })}
        />
        <Field
          label="Corporate ID"
          required
          value={draft.corporateId}
          onChange={(v) => setDraft({ ...draft, corporateId: v })}
          help="See format in Sandvik Information Service Hub"
        />
        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
            CRA role <span className="text-red-500">*</span>
          </label>
          <select
            value={draft.role}
            onChange={(e) =>
              setDraft({
                ...draft,
                role: e.target.value as CRAEconomicOperatorRole,
                mainEstablishment:
                  e.target.value === "manufacturer"
                    ? draft.mainEstablishment
                    : "",
              })
            }
            className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {draft.role === "manufacturer" && (
          <Field
            label="Manufacturer's main establishment"
            required
            value={draft.mainEstablishment}
            onChange={(v) => setDraft({ ...draft, mainEstablishment: v })}
          />
        )}
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
        >
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  help,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  help?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      {help && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          {help}
        </p>
      )}
    </div>
  );
}
